import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Separator } from '~/components';
import { Button } from '~/components/Button';
import { FormFieldCombobox } from '~/components/FormField';
import { FormFieldRadioGroup } from '~/components/FormField/components/FormFieldRadioGroup';
import { useFilterDrawer } from '~/hooks/useFilterDrawer';
import { Drawer } from '~/modules/settings/components/Drawer';
import {
  EXTERNAL_ROLES,
  USER_STATUS,
} from '~/modules/settings/constants/Filter';
import type { UsersFilter } from '~/modules/settings/types/filter';
import { translateOptions } from '~/modules/settings/utils/helpers';

const defaultValues: UsersFilter = {
  roles: [],
};

interface LabelUserFormFilterProps {
  open: boolean;
  onApply: () => void;
}

export function LabelUserFormFilter({
  open,
  onApply,
}: LabelUserFormFilterProps) {
  const { t } = useTranslation('settings');

  const filterForm = useForm<UsersFilter>({
    defaultValues,
  });

  const { handleApply, handleClear } = useFilterDrawer<UsersFilter>({
    open,
    onApply,
    filterForm,
  });

  return (
    <FormProvider {...filterForm}>
      <Drawer.Header>
        <Drawer.Title>{t('filters')}</Drawer.Title>
      </Drawer.Header>
      <Drawer.Body>
        <FormFieldRadioGroup
          name="isActive"
          label={t('userStatus')}
          options={translateOptions(USER_STATUS, t)}
        />
        <Separator className="my-md" />
        <FormFieldCombobox
          name="roles"
          label={t('role')}
          multiselect={true}
          displaySelectedOptions={true}
          options={translateOptions(EXTERNAL_ROLES, t)}
          placeholder={t('dropdowns.role.placeholder', {
            count: 2,
          })}
          searchPlaceholder={t('dropdowns.role.searchPlaceholder', {
            count: 2,
          })}
        />
      </Drawer.Body>
      <Drawer.Footer>
        <Button
          size="large"
          variant="tertiary"
          onClick={handleClear}
          className="font-normal"
          type="button"
        >
          {t('Clear filters')}
        </Button>
        <Button
          size="large"
          type="button"
          onClick={filterForm.handleSubmit(handleApply)}
        >
          {t('Apply')}
        </Button>
      </Drawer.Footer>
    </FormProvider>
  );
}

LabelUserFormFilter.displayName = 'LabelUserFormFilter';
